import epsClient from 'enrollment-platform/api/eps.client'

// Both compares (CSV vs live, Ideon fetch vs fetch) share one run lifecycle on
// the backend (plan_compare_runs): a start endpoint per source creates a run,
// then these shared endpoints poll, resume and cancel it.
const RUNS_BASE_PATH = '/api/v1/plan-management-v2/compare-data/runs'

export type CompareRunSource = 'CSV' | 'IDEON'

// Cancel deletes the run, so there's no CANCELLED status.
export type CompareRunStatus = 'RUNNING' | 'COMPLETED' | 'FAILED'

export type CompareRun = {
  runId: string
  source: CompareRunSource
  status: CompareRunStatus
  /** 0–100 */
  progress: number
  /** Current step while RUNNING, e.g. "Saving results". */
  stage: string | null
  year: number | null
  /** CSV runs only. */
  dataType: string | null
  /** IDEON runs only. */
  baseTaskId: string | null
  targetTaskId: string | null
  /**
   * COMPLETED: the result counts (CSV: totals; IDEON: per data type per
   * state). FAILED CSV: { errorCount }. Shape depends on `source`.
   */
  summary: Record<string, unknown> | null
  errorMessage: string | null
  createdAt: string
  completedAt: string | null
}

export type StartCompareRunResponse = {
  runId: string
  /** COMPLETED = a saved result opened instantly (Ideon pairs compared before). */
  status: 'RUNNING' | 'COMPLETED'
}

/** 404 = the run no longer exists (cancelled, or an Ideon fetch deleted). */
export async function getCompareRun(runId: string): Promise<CompareRun> {
  const { data } = await epsClient.get<CompareRun>(
    `${RUNS_BASE_PATH}/${runId}`,
    {
      // Polled while a run is in progress — never serve it from a cache, or the
      // UI can get stuck on a stale RUNNING state. The timestamp busts caches
      // (proxies) that ignore the request headers.
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      params: { _: Date.now() },
    }
  )
  return data
}

/** The caller's own in-progress run of a source, for resuming after refresh. */
export async function getActiveCompareRun(
  source: CompareRunSource
): Promise<{ runId: string | null }> {
  const { data } = await epsClient.get<{ runId: string | null }>(
    `${RUNS_BASE_PATH}/active`,
    { params: { source } }
  )
  return data
}

/** Deletes the run and its rows; 404 when it's no longer running. */
export async function cancelCompareRun(runId: string): Promise<void> {
  await epsClient.post(`${RUNS_BASE_PATH}/${runId}/cancel`)
}
